import type { ChartData } from './types';

/**
 * The built-in chart library.
 *
 * Each template is a pure function of ChartData, so any template can render any
 * query result. Templates infer their label and measure columns rather than
 * requiring per-chart configuration — that is what makes the gallery work
 * against a catalogue of 60+ backend charts without hand-wiring each one.
 *
 * Colours come from CSS custom properties (--c1 … --c6) defined in styles.css,
 * so a chart follows the platform's light/dark theme and the palette can be
 * swapped in one place.
 */

export interface Template {
  id: string;
  name: string;
  description: string;
  /** Rough guidance so the gallery can suggest a sensible default. */
  suits: string;
  render: (data: ChartData) => JSX.Element;
}

/* ------------------------------- helpers --------------------------------- */

const isNumeric = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** First column whose values are mostly numbers is treated as the measure. */
const measureIndex = (data: ChartData): number => {
  const sample = data.rows.slice(0, 20);
  for (let i = data.columns.length - 1; i >= 0; i -= 1) {
    const numeric = sample.filter((r) => isNumeric(r[i])).length;
    if (sample.length > 0 && numeric / sample.length > 0.6) return i;
  }
  return data.columns.length - 1;
};

/** First non-measure column is the label. */
const labelIndex = (data: ChartData, measure: number): number => {
  for (let i = 0; i < data.columns.length; i += 1) {
    if (i !== measure) return i;
  }
  return 0;
};

const fmt = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (isNumeric(v)) {
    const abs = Math.abs(v);
    if (abs >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)}B`;
    if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
    return Number.isInteger(v) ? String(v) : v.toFixed(2);
  }
  return String(v);
};

const Empty = ({ message = 'No data for this selection' }: { message?: string }) => (
  <div className="chart-empty">{message}</div>
);

const SERIES = ['var(--c1)', 'var(--c2)', 'var(--c3)', 'var(--c4)', 'var(--c5)', 'var(--c6)'];

/* ------------------------------ templates -------------------------------- */

const BarChart = ({ data }: { data: ChartData }) => {
  if (!data.rows.length) return <Empty />;

  const m = measureIndex(data);
  const l = labelIndex(data, m);
  const rows = data.rows.slice(0, 20);
  const values = rows.map((r) => (isNumeric(r[m]) ? r[m] : 0));
  const max = Math.max(...values, 0) || 1;

  return (
    <div className="tpl-bar">
      {rows.map((row, i) => (
        <div className="tpl-bar__row" key={i}>
          <span className="tpl-bar__label" title={String(row[l])}>
            {fmt(row[l])}
          </span>
          <span className="tpl-bar__track">
            <span
              className="tpl-bar__fill"
              style={{ width: `${(values[i] / max) * 100}%`, background: SERIES[0] }}
            />
          </span>
          <span className="tpl-bar__value">{fmt(row[m])}</span>
        </div>
      ))}
    </div>
  );
};

const LineChart = ({ data }: { data: ChartData }) => {
  if (data.rows.length < 2) return <Empty message="Not enough points to draw a line" />;

  const m = measureIndex(data);
  const l = labelIndex(data, m);
  const values = data.rows.map((r) => (isNumeric(r[m]) ? r[m] : 0));
  const max = Math.max(...values);
  const min = Math.min(...values);
  // A perfectly flat series would divide by zero — draw it down the middle.
  const span = max - min || 1;

  const W = 600;
  const H = 220;
  const pad = 8;
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * (W - pad * 2) + pad;
    const y = H - pad - ((v - min) / span) * (H - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  return (
    <div className="tpl-line">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img">
        <title>{data.columns[m]} over {data.columns[l]}</title>
        <polyline className="tpl-line__area" points={`${pad},${H} ${points.join(' ')} ${W - pad},${H}`} />
        <polyline className="tpl-line__stroke" points={points.join(' ')} />
      </svg>
      <div className="tpl-line__axis">
        <span>{fmt(data.rows[0][l])}</span>
        <span>{fmt(data.rows[data.rows.length - 1][l])}</span>
      </div>
    </div>
  );
};

const Table = ({ data }: { data: ChartData }) => {
  if (!data.rows.length) return <Empty />;
  return (
    <div className="tpl-table">
      <table>
        <thead>
          <tr>
            {data.columns.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.slice(0, 100).map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j} className={isNumeric(cell) ? 'num' : undefined}>
                  {fmt(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const BigNumber = ({ data }: { data: ChartData }) => {
  if (!data.rows.length) return <Empty />;
  const m = measureIndex(data);
  // Sum the measure so the tile is meaningful whether the query returns one row
  // or many.
  const total = data.rows.reduce<number>((acc, r) => acc + (isNumeric(r[m]) ? r[m] : 0), 0);
  return (
    <div className="tpl-bignumber">
      <div className="tpl-bignumber__value">{fmt(total)}</div>
      <div className="tpl-bignumber__label">{data.columns[m]}</div>
    </div>
  );
};

const Donut = ({ data }: { data: ChartData }) => {
  if (!data.rows.length) return <Empty />;

  const m = measureIndex(data);
  const l = labelIndex(data, m);
  const rows = data.rows.slice(0, 6);
  const values = rows.map((r) => (isNumeric(r[m]) ? r[m] : 0));
  const total = values.reduce((a, b) => a + b, 0);

  if (total <= 0) return <Empty message="No positive values to chart" />;

  // Build the ring with a conic-gradient so no arc maths or SVG paths are needed.
  let cursor = 0;
  const stops = values.map((v, i) => {
    const start = (cursor / total) * 100;
    cursor += v;
    const end = (cursor / total) * 100;
    return `${SERIES[i % SERIES.length]} ${start.toFixed(2)}% ${end.toFixed(2)}%`;
  });

  return (
    <div className="tpl-donut">
      <div className="tpl-donut__ring" style={{ background: `conic-gradient(${stops.join(',')})` }}>
        <div className="tpl-donut__hole">{fmt(total)}</div>
      </div>
      <ul className="tpl-donut__legend">
        {rows.map((row, i) => (
          <li key={i}>
            <span className="swatch" style={{ background: SERIES[i % SERIES.length] }} />
            <span className="name">{fmt(row[l])}</span>
            <span className="val">{fmt(row[m])}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Leaderboard = ({ data }: { data: ChartData }) => {
  if (!data.rows.length) return <Empty />;
  const m = measureIndex(data);
  const l = labelIndex(data, m);
  return (
    <ol className="tpl-leaderboard">
      {data.rows.slice(0, 25).map((row, i) => (
        <li key={i}>
          <span className="rank">{i + 1}</span>
          <span className="name" title={String(row[l])}>
            {fmt(row[l])}
          </span>
          <span className="val">{fmt(row[m])}</span>
        </li>
      ))}
    </ol>
  );
};

export const TEMPLATES: Template[] = [
  {
    id: 'bar',
    name: 'Bar chart',
    description: 'Horizontal bars, one per row, scaled to the largest value.',
    suits: 'Comparing a measure across categories',
    render: (data) => <BarChart data={data} />,
  },
  {
    id: 'line',
    name: 'Trend line',
    description: 'A filled line across every row in order.',
    suits: 'A measure over time',
    render: (data) => <LineChart data={data} />,
  },
  {
    id: 'table',
    name: 'Table',
    description: 'Every column, first 100 rows, numbers right-aligned.',
    suits: 'Detail and exact values',
    render: (data) => <Table data={data} />,
  },
  {
    id: 'bignumber',
    name: 'KPI tile',
    description: 'The measure summed to a single headline figure.',
    suits: 'One number that matters',
    render: (data) => <BigNumber data={data} />,
  },
  {
    id: 'donut',
    name: 'Donut',
    description: 'Share of total across the top six rows.',
    suits: 'Composition of a whole',
    render: (data) => <Donut data={data} />,
  },
  {
    id: 'leaderboard',
    name: 'Leaderboard',
    description: 'Ranked list with the measure alongside each entry.',
    suits: 'Top-N rankings',
    render: (data) => <Leaderboard data={data} />,
  },
];

export const getTemplate = (id: string | undefined): Template =>
  TEMPLATES.find((t) => t.id === id) ?? TEMPLATES[0];
